import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { DatasetResource, ExtractResource } from "@prequel/import-react";
import { useDataset, useExtract } from "@prequel/import-react";
import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type ExtractContext = {
  extract: ExtractResource;
  dataset: DatasetResource | undefined;
  isDatasetLoading: boolean;
};

export function useExtractContext(): ExtractContext {
  return useOutletContext<ExtractContext>();
}

const ExtractPage = () => {
  const datasetID = useRequiredParam("datasetID");
  const extractID = useRequiredParam("extractID");
  const { addToast } = useToast();
  const { data: extract, error, isLoading } = useExtract(extractID);
  const { data: dataset, isLoading: isDatasetLoading } = useDataset(datasetID);
  const navigate = useNavigate();
  const basePath = `/import/datasets/${datasetID}/extracts/${extractID}`;

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load extract job details." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!extract) {
    return <Navigate to={`/import/datasets/${datasetID}`} replace />;
  }

  const tabs = [
    <NavLink key="Details" to={basePath} className={checkTabIsActive} end>
      Details
    </NavLink>,
    <NavLink key="Records" to={`${basePath}/records`} className={checkTabIsActive} end>
      Records
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title="Extract Job"
      dynamicLabel={dataset ? dataset.name : "Dataset Details"}
      onGoBack={() => navigate(`/import/datasets/${datasetID}`)}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={extract.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ extract, dataset, isDatasetLoading }} />
    </DetailPageLayout>
  );
};

export default ExtractPage;
